import { initAxios } from '../../../utils/axiosHelper';
import { routeFor } from '../../EntityServiceAPI';
import type { IAdverseMedia, IBeneficialOwnershipResult } from './ai.d';

// eslint-disable-next-line no-undef
const PYAPI_BASE_URL = process.env.REACT_APP_PYAPI_URL || '/pyapi';

export interface IRunAdverseMediaParams {
  entityName: string;
  country?: string;
  aliases?: string[];
  databaseEntityId?: string;
  databaseId?: string;
  entityId?: string;
}

/**
 * Runs the OSINT adverse-media check and persists the result to the
 * DatabaseEntity record, via `business-checks/verify-adverse-media` on the
 * entity service.
 *
 * This is the only adverse-media path in the client. There is deliberately no
 * direct-to-pyapi `/screening/osint` variant: the entity service runs that exact
 * search server-side and unwraps the `combined` block before persisting, so a
 * second client-side call would bill the same AI web search twice. If this
 * throws, surface the error -- do not retry against pyapi.
 */
export const runAndPersistAdverseMedia = async(
  params: IRunAdverseMediaParams,
): Promise<IAdverseMedia | null> => {
  const { baseUrl, path } = routeFor('verifyAdverseMedia');
  const apiClient = await initAxios(baseUrl);
  if (!apiClient) {
    throw new Error('Failed to initialize entity API client');
  }

  try {
    const { data } = await apiClient.post<IAdverseMedia>(
      path,
      params,
      {
        timeout: 60000,
      },
    );
    return data ?? null;
  } catch (err: any) {
    const message =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      'Error running adverse media check';
    console.error('Error running adverse media check:', err);
    throw new Error(message);
  }
};

const normalizeBeneficialOwnershipResponse = (
  data: any,
): IBeneficialOwnershipResult | null => {
  if (!data) {
    return null;
  }

  const rawTopMatches = data.top_matches;
  const topMatches = Array.isArray(rawTopMatches)
    ? rawTopMatches.map((match: any) =>
        typeof match === 'string' ? match : match?.name || String(match),
      )
    : rawTopMatches
      ? [ String(rawTopMatches) ]
      : [];

  return {
    ...data,
    summary: data.summary || data.beneficial_ownership_summary,
    beneficial_ownership_summary:
      data.beneficial_ownership_summary || data.summary,
    top_matches: topMatches,
  };
};

export const getBeneficialOwnership = async(
  entityName: string,
  country?: string,
  address?: string,
  records?: any[],
  potentialMatchCount?: number,
): Promise<IBeneficialOwnershipResult | null> => {
  try {
    const instance = await initAxios(PYAPI_BASE_URL);
    if (!instance) {
      return null;
    }

    // The agent's `results` item schema requires snake_case fields (at minimum
    // `name` and `list_name`). The records we retrieve carry the raw BITE field
    // names ("Name of Entity", "List Name", ...), so map those onto the
    // expected keys while keeping the original fields for any extra context.

    const results = (records ?? []).map((record: any) => ({
    ...record,
    name:
      record?.['Name of Entity'] ||
      record?.['Related Entity'] ||
      record?.['Name of Interested Party'] ||
      record?.name ||
      '',
    list_name: record?.['List Name'] || record?.list_name || '',
    country: record?.['Entity Country'] || record?.country || '',
    address: record?.['Entity Address'] || record?.address || '',
    related_entity:
      record?.['Related Entity'] ||
      record?.related_entity ||
      '',
}));

    //const results = (records ?? []).map((record: any) => ({
      //...record,
      //name:
        //record?.['Name of Entity'] ||
        //record?.['Related Entity'] ||
        //record?.name ||
        //'',
      //list_name: record?.['List Name'] || record?.list_name || '',
    //}));

    const requestData = {
      entity: {
        name: entityName,
        country: country,
        address: address,
      },
      // BOI/PEP matches retrieved from the bite_app_entities_boi_and_peps index
      // (shown in the Ownership tab) so the agent can ground its analysis. The
      // PyAPI agent expects this list under the `results` key.
      results: results,
      // Use the Ownership tab's total rather than a deduplicated name count or
      // the number of records retained for summarization.
      potential_match_count: potentialMatchCount,
    };

    const resp = await instance.post('/screening/boi', requestData);
    return normalizeBeneficialOwnershipResponse(resp.data);
  } catch (err) {
    console.error('Error fetching beneficial ownership:', err);
    return null;
  }
};
